import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import {
  type ImageTextStackedXup,
  ImageVariant,
} from '@/components/HighlightImageTextStacked/HighlightImageTextStacked';
import useSharedStyles from '@/components/HighlightImageTextStacked/SharedStackedStyles.styles';
import { getImages } from '@/components/HighlightImageTextStacked/utils';
import ArtDirectedImage from '@/components/Image/ArtDirectedImage';
import Link from '@/components/Link/Link';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';
import { brandColors } from '@/styles/theme/values/brandColors';

import useStyles from './MultipleStacks.styles';

type MultipleStacksProps = {
  content: ImageTextStackedXup;
  variant: string;
  index: number;
  shouldShowHeaderCTA: boolean | undefined;
  isOnDark: boolean;
};

const MultipleStacks = ({
  content,
  variant,
  index,
  shouldShowHeaderCTA,
  isOnDark,
}: MultipleStacksProps): React.JSX.Element => {
  // biome-ignore lint/suspicious/noImplicitAnyLet: Automated migration to Biome
  let imageClass;
  const imageStack = content?.fields;
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const { classes, cx } = useStyles({ isOnDark });
  const { classes: sharedClasses } = useSharedStyles({ isOnDark });

  const { isLarge, isExtraLarge } = useViewPortSize();
  const isDesktop = isLarge || isExtraLarge;

  const images = imageStack?.backgroundImages
    ? getImages(imageStack?.backgroundImages)
    : undefined;

  switch (variant) {
    case ImageVariant['2UP']:
      imageClass = classes.doubleImage;
      break;
    case ImageVariant['3UP']:
      imageClass = classes.tripleImage;
      break;
    case ImageVariant['4UP']:
      imageClass = classes.quadrupleImage;
      break;
    default:
      imageClass = classes.doubleImage;
      break;
  }

  let url = '';

  if (imageStack.cta?.fields?.url) {
    url = imageStack.cta?.fields?.url;
  }

  const is2UP = variant === '2UP';
  const addLink = ((is2UP && shouldShowHeaderCTA) || !is2UP) && url !== '';
  const isSecondRow = index === 2 || index === 3;

  return (
    <div>
      {images && (
        <ArtDirectedImage
          className={cx(sharedClasses.image, sharedClasses.media, imageClass)}
          images={images}
          alt=''
          role='presentation'
        />
      )}
      <div
        className={cx(
          classes.stackText,
          is2UP && isDesktop && classes.bodyContainer
        )}
      >
        {imageStack?.subhead && (
          <h3 className={cx(globalClasses.h4, sharedClasses.subhead)}>
            {addLink ? (
              <Link className={classes.link} href={url}>
                {imageStack.subhead}
              </Link>
            ) : (
              imageStack.subhead
            )}
          </h3>
        )}

        {imageStack?.body && (
          <div
            className={cx(
              is2UP && classes.bodyTwoUp,
              !is2UP && !isSecondRow && classes.stackedBody,
              globalClasses.body
            )}
            style={{
              color: isOnDark
                ? brandColors.tintsOffWhite40
                : brandColors.tintsNeutral20,
            }}
          >
            {imageStack.body}
          </div>
        )}
      </div>
      {imageStack?.cta && is2UP && !shouldShowHeaderCTA && (
        <Button
          key={`button-id${index}${imageStack?.cta?.sys?.id}`}
          isOnDark={isOnDark}
          variant={ButtonVariant.SECONDARY}
          label={imageStack?.cta?.fields?.label}
          href={imageStack?.cta?.fields?.url}
          className={sharedClasses.cta}
          size={{ xs: ButtonSize.SM, sm: ButtonSize.MD }}
        />
      )}
    </div>
  );
};

export default MultipleStacks;
